import fetchJson, { FetchJsonMethodName } from './fetchJson';
import PATHS from 'app/utils/paths';
import { processDeauthentication } from 'app/services/auth';
import { invertKpiValues } from 'app/utils/helpers';
import {
  URLParams,
  getUrl,
  getQueryString,
  getNullableQueryParam,
} from './url';

interface RequestOptions {
  headers: Record<string, string>;
  params?: { include?: string };
}

type RequestFn = (
  url: string,
  body?: any,
  options?: RequestOptions
) => Promise<any>;

type UnauthorizedErrorHandler = (response: Response) => Promise<any>;

let unauthorizedErrorHandler: UnauthorizedErrorHandler;

export const setUnauthorizedErrorHandler = (
  handler: UnauthorizedErrorHandler
) => (unauthorizedErrorHandler = handler);

let unauthorizedErrorHandlerPromise: null | Promise<void> = null;

const requestMethods: FetchJsonMethodName[] = [
  'get',
  'post',
  'put',
  'patch',
  'delete',
  'head',
];

export const request: Record<FetchJsonMethodName, RequestFn> =
  requestMethods.reduce(
    (request, method) => {
      request[method] = (url: string, body?: any, options?: RequestOptions) => {
        if (method === 'get' && body && !options) {
          options = body;
          body = null;
        }
        if (!/^http/.test(url)) {
          url = getUrl(url, options as URLParams);
        }
        return Promise.resolve(unauthorizedErrorHandlerPromise)
          .then(() => fetchJson[method](url, body, options))
          .catch((response: Response) => {
            if (response.status === 401 && unauthorizedErrorHandler) {
              unauthorizedErrorHandlerPromise =
                unauthorizedErrorHandlerPromise ||
                unauthorizedErrorHandler(response);
              return unauthorizedErrorHandlerPromise
                .then(() => {
                  if (options?.headers?.authorization) {
                    delete options.headers.authorization;
                  }
                  return fetchJson[method](url, body, options);
                })
                .catch((response: Response) => {
                  if (response.status === 401) {
                    // We've got 401 again either during refreshing the token or
                    // retrying request, we have no option now but to kick the
                    // user to login screen.
                    processDeauthentication(window.location.origin);
                  }
                })
                .finally(() => (unauthorizedErrorHandlerPromise = null));
            }
            throw response;
          });
      };
      return request;
    },
    {} as Record<FetchJsonMethodName, RequestFn>
  );

export interface Company {
  id: number;
  parentId: number | null;
  name: string;
  key: string;
  locale: string;
  currency: string;
}
export interface Control {
  id: number;
  parentId: number | null;
  name: string;
  displayOrder: number;
  tagKey?: string;
  tagId?: number;
  tags?: TagType[];
}

export enum KPIGroupHints {
  Default,
  HasSubgroups,
  HasRepeatSubgroups,
  RepeatKPIsForThreatSurface,
  RepeatKPIsForThreatLevel,
  RepeatKPIsForCapability,
}

export interface KPIGroup {
  id: number;
  parentId: number | null;
  hint: KPIGroupHints;
  key: string;
  path: string;
  name: string;
  helpURI?: string;
  weight: number;
  displayOrder: number;
  subGroups?: KPIGroup[];
}

export interface SecurityAssetContextView {
  id: number;
  key: string;
  controlId: number;
  levelId: number | null;
  surfaceId: number | null;
  context: SecurityAssetContextItem[] | null;
  contextURI: string | null;
  userId: number;
  companyId: number;
  createdTime?: string;
}

export interface SecurityAssetContextItem {
  key: string;
  uri: string;
  value: string;
  assetId?: number;
  assignmentId?: number;
  attachmentId?: number | null;
  assignmentCount?: number;
  shortDescription?: string | null;
  longDescription?: string | null;
}

export interface KPIMetadata {
  id: number;
  companyId: number;
  key: string;
  name: string;
  valueTypeId: number;
  displayOrder: number;
  valueType?: KPIValueType;
}

export interface User {
  id: number;
  companyId: number;
  name: string;
  oauth_id: string;
}

export interface ActivityPerson {
  id: number;
  companyId: number;
  userId: number | null;
  displayName: string;
  email: string;
  title: string | null;
  isActive: boolean;
}

export interface ActivityAssetAssociation {
  assetId: number;
  title: string;
  assetType: string;
  status: string;
  updatedTime: string;
  relationship: 'owner' | 'creator';
}

export interface ActivityAssetActivity {
  historyId: number;
  time: string;
  actionType: string;
  payload: Record<string, unknown> | null;
  assetId: number;
  assetTitle: string;
  contextSummary: string;
}

export interface ActivityKpiEntry {
  kpiValueId: number;
  time: string;
  value: string | number | null;
  kpiId: number;
  kpiName: string;
  kpiKey: string;
  controlId: number;
  controlName: string;
  surfaceId?: number | null;
  surfaceName?: string | null;
  levelId?: number | null;
  levelName?: string | null;
}

export interface AccountAnalysis {
  person: ActivityPerson;
  user: User | null;
  assetAssociations: {
    owned: ActivityAssetAssociation[];
    created: ActivityAssetAssociation[];
  };
  assetActivity: ActivityAssetActivity[];
  kpiActivity: ActivityKpiEntry[];
}
export interface ControlCapability {
  id: number;
  name: string;
  controlId: number;
  surfaceTypeId: number;
  levelId: number;
  displayOrder: number;
}

export enum KPIValueTypes {
  Text = 'text',
  Decimal = 'decimal',
  Integer = 'integer',
  Currency = 'currency',
  Percentage = 'percentage',
  Likert = 'likert',
}

export interface KPIValue {
  id: number;
  kpiId: number;
  controlId: number;
  levelId?: number;
  surfaceId?: number;
  userId?: number;
  companyId?: number;
  value: string | number | null;
  kpi: KPI;
  date?: string;
  createdTime?: string;
}

export interface KPIValueType {
  id: number;
  key: KPIValueTypes;
  name: string;
}

export interface KPI {
  id: number;
  groupId: number;
  key: string;
  name: string;
  description?: string | null;
  weight: number;
  helpURI?: string;
  valueType: KPIValueType;
  kpiValue: KPIValue[];
  displayOrder: number;
}

export type BudgetEnvironmentSummary = {
  name: string;
  percent: number;
};

export type BudgetSummary = {
  totalBudget: number;
  capexTotal: number;
  opexTotal: number;
  peopleTotal: number;
  technologyTotal: number;
  vendorTotal: number;
  environmentCount: number;
  environmentBreakdown: BudgetEnvironmentSummary[];
};

export type ControlResourceType = 'people' | 'technology' | 'services';
export type ControlResourceStatus = 'present' | 'planned';
export type ControlResourceThreatTier = 'low' | 'moderate' | 'high';
export type ControlResourceAssociationType = 'direct' | 'project_backed';

export interface ControlResourceSurface {
  id: number;
  name: string;
  displayOrder: number;
}

export interface ControlResource {
  id: number;
  resourceId: number;
  controlId: number;
  companyId: number;
  name: string;
  description: string | null;
  resourceType: ControlResourceType;
  ownerPersonId?: number | null;
  associationType: ControlResourceAssociationType;
  projectName: string | null;
  status: ControlResourceStatus;
  currentBudget: number;
  targetBudget: number;
  currency: string;
  cxOxSplit: number;
  maxThreatTier: ControlResourceThreatTier;
  isActive: boolean;
  updatedByUserId?: number | null;
  updatedByUser?: ResourceActor | null;
  resourceUpdatedTime?: string | null;
  resourceUpdatedByUserId?: number | null;
  resourceUpdatedByUser?: ResourceActor | null;
  surfaces: ControlResourceSurface[];
  createdTime: string;
  updatedTime: string;
}

export interface ControlScope {
  id: number;
  controlId: number;
  surfaceId: number;
  isInScope: boolean;
  createdTime: string;
  updatedTime: string;
}

export interface SaveControlResourcePayload {
  id?: number;
  controlId: number;
  name: string;
  description?: string | null;
  resourceType: ControlResourceType;
  associationType: ControlResourceAssociationType;
  projectName?: string | null;
  status: ControlResourceStatus;
  currentBudget: number;
  targetBudget: number;
  currency?: string;
  cxOxSplit: number;
  maxThreatTier: ControlResourceThreatTier;
  isActive: boolean;
  surfaceIds: number[];
}

export type ResourceType = 'people' | 'technology' | 'services';
export type ResourceSubjectType = 'control' | 'project' | 'management';
export type ResourceLifecycle = 'active' | 'planned' | 'retired';
export type ResourceThreatTier = 'low' | 'moderate' | 'high';
export type ResourceAssociationType = 'direct' | 'project_backed';

export interface ResourceActor {
  id: number;
  name: string;
}

export interface Resource {
  id: number;
  companyId: number;
  name: string;
  description: string | null;
  resourceType: ResourceType;
  ownerPersonId: number | null;
  ownerName?: string | null;
  lifecycle: ResourceLifecycle;
  currentBudget: number;
  targetBudget: number;
  highThreatBaselineBudget?: number | null;
  contractStartDate?: string | null;
  contractEndDate?: string | null;
  currency: string;
  cxOxSplit: number;
  assignmentCount?: number;
  updatedByUserId?: number | null;
  updatedByUser?: ResourceActor | null;
  createdTime: string;
  updatedTime: string;
}

export type ResourceHistoryActionType =
  | 'created'
  | 'details_changed'
  | 'owner_changed'
  | 'lifecycle_changed'
  | 'budget_changed'
  | 'contract_changed';

export interface ResourceHistory {
  id: number;
  resourceId: number;
  companyId: number;
  actionType: ResourceHistoryActionType;
  changedByUserId?: number | null;
  changedByUser?: ResourceActor | null;
  changedTime: string;
  payload: Record<string, unknown> | null;
}

export interface SaveResourcePayload {
  id?: number;
  name: string;
  description?: string | null;
  resourceType: ResourceType;
  ownerPersonId?: number | null;
  lifecycle: ResourceLifecycle;
  currentBudget: number;
  targetBudget: number;
  highThreatBaselineBudget?: number | null;
  contractStartDate?: string | null;
  contractEndDate?: string | null;
  currency: string;
  cxOxSplit: number;
}

export interface ResourceAssignmentSurface {
  id: number;
  name: string;
  displayOrder: number;
}

export interface ResourceAssignment {
  id: number;
  resourceId: number;
  companyId: number;
  subjectType: ResourceSubjectType;
  subjectId: number | null;
  projectId: number | null;
  subjectName: string | null;
  associationType: ResourceAssociationType;
  lifecycle: ResourceLifecycle;
  currentBudget: number;
  targetBudget: number;
  cxOxSplit: number;
  maxThreatTier: ResourceThreatTier;
  isActive: boolean;
  updatedByUserId?: number | null;
  updatedByUser?: ResourceActor | null;
  createdTime: string;
  updatedTime: string;
  resource?: {
    id: number;
    name: string;
    description: string | null;
    resourceType: ResourceType;
    lifecycle: ResourceLifecycle;
    currency: string;
    cxOxSplit: number;
    updatedTime?: string | null;
    updatedByUserId?: number | null;
    updatedByUser?: ResourceActor | null;
  };
  surfaces: ResourceAssignmentSurface[];
}

export type ResourceAssignmentHistoryActionType =
  | 'assignment_created'
  | 'assignment_changed'
  | 'assignment_removed'
  | 'budget_changed'
  | 'lifecycle_changed';

export interface ResourceAssignmentHistory {
  id: number;
  resourceAssignmentId: number | null;
  resourceId: number;
  companyId: number;
  actionType: ResourceAssignmentHistoryActionType;
  changedByUserId?: number | null;
  changedByUser?: ResourceActor | null;
  changedTime: string;
  payload: Record<string, unknown> | null;
}

export interface SaveResourceAssignmentPayload {
  id?: number;
  resourceId: number;
  subjectType: ResourceSubjectType;
  subjectId?: number | null;
  projectId?: number | null;
  associationType: ResourceAssociationType;
  currentBudget: number;
  targetBudget: number;
  maxThreatTier: ResourceThreatTier;
  isActive: boolean;
  surfaceIds: number[];
}

export interface ResourceSecurityAssetLink {
  id: number;
  companyId: number;
  resourceId: number;
  securityAssetId: number;
  createdByUserId: number;
  createdTime: string;
  updatedTime: string;
  resource: {
    id: number;
    name: string;
    resourceType: ResourceType;
    lifecycle: ResourceLifecycle;
    ownerPersonId: number | null;
  };
  securityAsset: {
    id: number;
    title: string;
    assetType: string;
    status: string;
    ownerPersonId: number | null;
  };
}

export interface SaveResourceSecurityAssetLinkPayload {
  resourceId: number;
  securityAssetId: number;
}

export type ProjectType =
  | 'security'
  | 'operations'
  | 'technology'
  | 'compliance'
  | 'transformation'
  | 'other';

export type ProjectStatus =
  | 'proposed'
  | 'approved'
  | 'active'
  | 'paused'
  | 'completed'
  | 'cancelled';

export interface Project {
  id: number;
  companyId: number;
  name: string;
  description: string | null;
  objective: string | null;
  projectType: ProjectType;
  status: ProjectStatus;
  ownerPersonId: number | null;
  sponsorPersonId: number | null;
  startDate: string | null;
  targetEndDate: string | null;
  actualEndDate: string | null;
  plannedBudget: number;
  approvedBudget: number;
  forecastBudget: number;
  actualBudget: number;
  currency: string;
  isActive: boolean;
  createdByUserId: number;
  updatedByUserId: number;
  createdTime: string;
  updatedTime: string;
  controlLinkCount?: number;
  resourceAssignmentCount?: number;
  assetLinkCount?: number;
  resourceCurrentBudget?: number;
  resourceTargetBudget?: number;
}

export interface SaveProjectPayload {
  id?: number;
  name: string;
  description?: string | null;
  objective?: string | null;
  projectType: ProjectType;
  status: ProjectStatus;
  ownerPersonId?: number | null;
  sponsorPersonId?: number | null;
  startDate?: string | null;
  targetEndDate?: string | null;
  actualEndDate?: string | null;
  plannedBudget: number;
  approvedBudget: number;
  forecastBudget: number;
  actualBudget: number;
  currency: string;
  isActive: boolean;
}

export type ProjectControlLinkRole =
  | 'affected'
  | 'primary'
  | 'dependency'
  | 'delivered_by';

export interface ProjectControlLink {
  id: number;
  projectId: number;
  companyId: number;
  controlId: number;
  relationshipRole: ProjectControlLinkRole;
  expectedImpact: string | null;
  notes: string | null;
  createdByUserId: number;
  updatedByUserId: number;
  createdTime: string;
  updatedTime: string;
  control?: {
    id: number;
    name: string | null;
    displayOrder: number | null;
  };
}

export interface SaveProjectControlLinkPayload {
  id?: number;
  projectId: number;
  controlId: number;
  relationshipRole: ProjectControlLinkRole;
  expectedImpact?: string | null;
  notes?: string | null;
}

export type ProjectSecurityAssetLinkRole =
  | 'affected'
  | 'primary'
  | 'dependency'
  | 'deliverable'
  | 'reference';

export interface ProjectSecurityAssetLink {
  id: number;
  projectId: number;
  companyId: number;
  securityAssetId: number;
  relationshipRole: ProjectSecurityAssetLinkRole;
  notes: string | null;
  createdByUserId: number;
  updatedByUserId: number;
  createdTime: string;
  updatedTime: string;
  securityAsset?: {
    id: number;
    key: string | null;
    title: string | null;
    assetType: string | null;
    status: string | null;
    sensitivity: string | null;
  };
}

export interface SaveProjectSecurityAssetLinkPayload {
  id?: number;
  projectId: number;
  securityAssetId: number;
  relationshipRole: ProjectSecurityAssetLinkRole;
  notes?: string | null;
}

type ApiRecord = {
  [key: string]: unknown;
};

const read = <T>(record: ApiRecord, ...keys: string[]): T | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined) {
      return value as T;
    }
  }
  return undefined;
};

const normalizeResourceSecurityAssetLink = (
  link: ApiRecord
): ResourceSecurityAssetLink => ({
  id: read<number>(link, 'id') || 0,
  companyId: read<number>(link, 'companyId', 'company_id') || 0,
  resourceId: read<number>(link, 'resourceId', 'resource_id') || 0,
  securityAssetId:
    read<number>(
      link,
      'securityAssetId',
      'secURItyAssetId',
      'security_asset_id'
    ) || 0,
  createdByUserId:
    read<number>(link, 'createdByUserId', 'created_by_user_id') || 0,
  createdTime: read<string>(link, 'createdTime', 'created_time') || '',
  updatedTime: read<string>(link, 'updatedTime', 'updated_time') || '',
  resource: read<ResourceSecurityAssetLink['resource']>(link, 'resource') || {
    id: read<number>(link, 'resourceId', 'resource_id') || 0,
    name: '',
    resourceType: 'technology',
    lifecycle: 'active',
    ownerPersonId: null,
  },
  securityAsset: read<ResourceSecurityAssetLink['securityAsset']>(
    link,
    'securityAsset',
    'secURItyAsset'
  ) || {
    id:
      read<number>(
        link,
        'securityAssetId',
        'secURItyAssetId',
        'security_asset_id'
      ) || 0,
    title: '',
    assetType: 'document',
    status: 'draft',
    ownerPersonId: null,
  },
});

export interface ControlBudgetTotals {
  controlId: number;
  currency: string;
  currentBudgetTotal: number;
  targetBudgetTotal: number;
  gapBudgetTotal: number;
  resourceCount: number;
  presentResourceCount: number;
  plannedResourceCount: number;
  linkedAssetCount: number;
  typeBreakdown: Record<
    ControlResourceType,
    {
      currentBudget: number;
      targetBudget: number;
    }
  >;
  spendProfile: {
    currentCxTotal: number;
    currentOxTotal: number;
    targetCxTotal: number;
    targetOxTotal: number;
  };
}

export interface ControlBudgetMatrixCell {
  surfaceId: number;
  threatTier: ControlResourceThreatTier;
  currentBudgetTotal: number;
  targetBudgetTotal: number;
  currentCxBudget: number;
  currentOxBudget: number;
  peopleBudget: number;
  technologyBudget: number;
  servicesBudget: number;
}

export interface ControlBudgetMatrixSummary {
  controlId: number;
  currency: string;
  tiers: ControlResourceThreatTier[];
  surfaces: ControlResourceSurface[];
  cells: ControlBudgetMatrixCell[];
}

export interface ControlBudgetCellBreakdownResource {
  id: number;
  resourceId: number;
  name: string;
  resourceType: ControlResourceType;
  associationType: ControlResourceAssociationType;
  projectName: string | null;
  status: ControlResourceStatus;
  environmentCount: number;
  reachableThreatTierCount: number;
  maxThreatTier: ControlResourceThreatTier;
  allocationDivisor: number;
  currentBudgetAllocated: number;
  targetBudgetAllocated: number;
}

export interface ControlBudgetCellBreakdown {
  controlId: number;
  surfaceId: number;
  threatTier: ControlResourceThreatTier;
  currency: string;
  currentBudgetTotal: number;
  targetBudgetTotal: number;
  resources: ControlBudgetCellBreakdownResource[];
}

// Auth related API calls use `fetchJson` directly to skip 401 handler
export const login = () => fetchJson.get(getUrl(PATHS.API.LOGIN));

export const logout = () => fetchJson.get(getUrl(PATHS.API.LOGOUT));

export const changePassword = () => fetchJson.get(getUrl(PATHS.API.PASSWORD));

export const getAuthToken = (authorizationCode: string) =>
  fetchJson.get(
    getUrl(PATHS.API.TOKEN, {
      query: {
        code: authorizationCode,
      },
    })
  );

export const refreshAuthToken = (refreshToken: string) =>
  fetchJson.get(
    getUrl(PATHS.API.TOKEN, {
      query: {
        grantType: 'refresh_token',
        refreshToken,
      },
    })
  );

export const getCompany = (
  id: string | number,
  options: RequestOptions
): Promise<Company> =>
  request
    .get(`${PATHS.API.COMPANY}/${id}${getQueryString(options.params)}`, options)
    .then((company: Company | Company[]) =>
      Array.isArray(company) ? company[0] : company
    );

export const getCompanies = (
  { parentId }: { parentId: number | string | null },
  options?: RequestOptions
): Promise<Company> =>
  request
    .get(PATHS.API.COMPANIES, {
      query: {
        ...{ parentId },
        ...options?.params,
      },
      ...options?.headers,
    })
    .then((company: Company | Company[]) =>
      Array.isArray(company) ? company[0] : company
    );

export const getUserById = (
  id: number,
  options: RequestOptions
): Promise<User> =>
  request.get(
    `${PATHS.API.USER}/${id}${getQueryString(options.params)}`,
    options
  );

export const getUsers = (
  {
    companyId,
    include,
  }: {
    companyId?: number | null;
    include?: string;
  } = {},
  options?: RequestOptions
): Promise<User[]> =>
  request.get(PATHS.API.USERS, {
    query: {
      ...getNullableQueryParam('companyId', companyId),
      ...getNullableQueryParam('include', include),
    },
    headers: options?.headers || {},
  });

export const getCurrentUser = (options: RequestOptions): Promise<User> =>
  request.get(`${PATHS.API.USER}${getQueryString(options.params)}`, options);

export const getAccountAnalysis = (
  personId: number,
  options?: RequestOptions
): Promise<AccountAnalysis> =>
  request.get(PATHS.API.ACTIVITY_ACCOUNT_ANALYSIS, {
    query: {
      personId,
    },
    headers: options?.headers || {},
  });

export const provisionCurrentUser = (options: RequestOptions): Promise<User> =>
  request.post(
    `${PATHS.API.USER}${getQueryString(options.params)}`,
    null,
    options
  );

export const updateCurrentUser = (options: RequestOptions): Promise<User> =>
  request.patch(
    `${PATHS.API.USER}${getQueryString(options.params)}`,
    null,
    options
  );

export const getControls = (
  tagKey?: string,
  include?: string
): Promise<Control[]> =>
  request
    .get(PATHS.API.CONTROLS, {
      query: {
        ...getNullableQueryParam('tagKey', tagKey),
        ...getNullableQueryParam('include', include),
      },
    })
    .then((controls) =>
      controls.sort(
        (prev: Control, next: Control) => prev.displayOrder - next.displayOrder
      )
    );

export const getControl = (
  id: string | number,
  include?: string
): Promise<Control> =>
  request
    .get(`${PATHS.API.CONTROL}/${id}`, {
      query: {
        ...getNullableQueryParam('include', include),
      },
    })
    .then((control: Control | Control[]) =>
      Array.isArray(control) ? control[0] : control
    );

const sortNestedGroups = (groups: KPIGroup[]): KPIGroup[] => {
  const result = groups.sort((a, b) => a.displayOrder - b.displayOrder);
  result.forEach((group: KPIGroup) => {
    if (group.subGroups) {
      sortNestedGroups(group.subGroups);
    }
  });
  return result;
};

export const getKPIGroups = (): Promise<KPIGroup[]> =>
  request
    .get(PATHS.API.KPI_GROUPS, {
      query: {
        parentId: null,
        include: 'subGroups',
      },
    })
    .then((groups) =>
      groups.map((grp: KPIGroup) => ({
        ...grp,
        path: grp.key.split('.').pop(),
      }))
    )
    .then((groups) => sortNestedGroups(groups));

export const getKPIGroupsByAttr = (
  parentId?: number | null,
  groupKey?: string | null,
  include?: string
): Promise<KPIGroup[]> =>
  request
    .get(PATHS.API.KPI_GROUPS, {
      query: {
        ...getNullableQueryParam('parentId', parentId),
        ...getNullableQueryParam('groupKey', groupKey),
        ...getNullableQueryParam('include', include),
      },
    })
    .then((groups) =>
      groups.map((grp: KPIGroup) => ({
        ...grp,
        path: grp.key.split('.').pop(),
      }))
    );

export const getControlCapabilities = (
  controlId: number,
  surfaceId?: number
): Promise<ControlCapability[]> =>
  request.get(
    getUrl(PATHS.API.CAPABILITIES, {
      query: {
        controlId,
        ...(surfaceId ? { surfaceId } : {}),
      },
    })
  );

export const getControlResources = (
  controlId: number
): Promise<ControlResource[]> =>
  request.get(PATHS.API.CONTROL_RESOURCES, {
    query: {
      controlId,
    },
  });

export const createControlResource = (
  resource: SaveControlResourcePayload
): Promise<ControlResource> =>
  request.post(PATHS.API.CONTROL_RESOURCE, resource);

export const updateControlResource = (
  resource: SaveControlResourcePayload & { id: number }
): Promise<ControlResource> =>
  request.put(PATHS.API.CONTROL_RESOURCE, resource);

export const getResources = ({
  q,
  resourceType,
  ownerPersonId,
  isActive,
  hasAssignments,
}: {
  q?: string;
  resourceType?: ResourceType;
  ownerPersonId?: number | null;
  isActive?: boolean;
  hasAssignments?: boolean;
} = {}): Promise<Resource[]> =>
  request.get(PATHS.API.RESOURCES, {
    query: {
      ...getNullableQueryParam('q', q),
      ...getNullableQueryParam('resourceType', resourceType),
      ...getNullableQueryParam('ownerPersonId', ownerPersonId),
      ...getNullableQueryParam(
        'isActive',
        isActive === undefined ? undefined : String(isActive)
      ),
      ...getNullableQueryParam(
        'hasAssignments',
        hasAssignments === undefined ? undefined : String(hasAssignments)
      ),
    },
  });

export const getResourceById = (id: number): Promise<Resource> =>
  request.get(`${PATHS.API.RESOURCE}/${id}`);

export const getResourceHistory = ({
  resourceId,
}: {
  resourceId?: number;
} = {}): Promise<ResourceHistory[]> =>
  request.get(PATHS.API.RESOURCE_HISTORY, {
    query: {
      ...getNullableQueryParam('resourceId', resourceId),
    },
  });

export const createResource = (
  resource: SaveResourcePayload
): Promise<Resource> => request.post(PATHS.API.RESOURCE, resource);

export const updateResource = (
  resource: SaveResourcePayload & { id: number }
): Promise<Resource> => request.put(PATHS.API.RESOURCE, resource);

export const patchResource = (
  resource: Partial<SaveResourcePayload> & { id: number }
): Promise<Resource> => request.patch(PATHS.API.RESOURCE, resource);

export const getResourceAssignments = ({
  resourceId,
  subjectType,
  subjectId,
  projectId,
  isActive,
}: {
  resourceId?: number;
  subjectType?: ResourceSubjectType;
  subjectId?: number;
  projectId?: number;
  isActive?: boolean;
} = {}): Promise<ResourceAssignment[]> =>
  request.get(PATHS.API.RESOURCE_ASSIGNMENTS, {
    query: {
      ...getNullableQueryParam('resourceId', resourceId),
      ...getNullableQueryParam('subjectType', subjectType),
      ...getNullableQueryParam('subjectId', subjectId),
      ...getNullableQueryParam('projectId', projectId),
      ...getNullableQueryParam(
        'isActive',
        isActive === undefined ? undefined : String(isActive)
      ),
    },
  });

export const getResourceAssignmentById = (
  id: number
): Promise<ResourceAssignment> =>
  request.get(`${PATHS.API.RESOURCE_ASSIGNMENT}/${id}`);

export const getResourceAssignmentHistory = ({
  resourceId,
  resourceAssignmentId,
}: {
  resourceId?: number;
  resourceAssignmentId?: number;
} = {}): Promise<ResourceAssignmentHistory[]> =>
  request.get(PATHS.API.RESOURCE_ASSIGNMENT_HISTORY, {
    query: {
      ...getNullableQueryParam('resourceId', resourceId),
      ...getNullableQueryParam('resourceAssignmentId', resourceAssignmentId),
    },
  });

export const createResourceAssignment = (
  payload: SaveResourceAssignmentPayload
): Promise<ResourceAssignment> =>
  request.post(PATHS.API.RESOURCE_ASSIGNMENT, payload);

export const updateResourceAssignment = (
  payload: SaveResourceAssignmentPayload & { id: number }
): Promise<ResourceAssignment> =>
  request.put(PATHS.API.RESOURCE_ASSIGNMENT, payload);

export const deleteResourceAssignment = (
  id: number
): Promise<Response | Error> =>
  request
    .delete(`${PATHS.API.RESOURCE_ASSIGNMENT}/${id}`)
    .then((response: Response) => response)
    .catch((error: Error) => error);

export const getResourceSecurityAssetLinks = ({
  resourceId,
  securityAssetId,
}: {
  resourceId?: number;
  securityAssetId?: number;
} = {}): Promise<ResourceSecurityAssetLink[]> =>
  request
    .get(PATHS.API.RESOURCE_SECURITY_ASSET_LINKS, {
      query: {
        ...getNullableQueryParam('resourceId', resourceId),
        ...getNullableQueryParam('securityAssetId', securityAssetId),
      },
    })
    .then((links: unknown) =>
      (links as ApiRecord[]).map(normalizeResourceSecurityAssetLink)
    );

export const createResourceSecurityAssetLink = (
  payload: SaveResourceSecurityAssetLinkPayload
): Promise<ResourceSecurityAssetLink> =>
  request
    .post(PATHS.API.RESOURCE_SECURITY_ASSET_LINK, payload)
    .then((link: unknown) =>
      normalizeResourceSecurityAssetLink(link as ApiRecord)
    );

export const deleteResourceSecurityAssetLink = (
  id: number
): Promise<Response | Error> =>
  request
    .delete(`${PATHS.API.RESOURCE_SECURITY_ASSET_LINK}/${id}`)
    .then((response: Response) => response)
    .catch((error: Error) => error);

export const getProjects = ({
  q,
  projectType,
  status,
  ownerPersonId,
  isActive,
}: {
  q?: string;
  projectType?: ProjectType;
  status?: ProjectStatus;
  ownerPersonId?: number | null;
  isActive?: boolean;
} = {}): Promise<Project[]> =>
  request.get(PATHS.API.PROJECTS, {
    query: {
      ...getNullableQueryParam('q', q),
      ...getNullableQueryParam('projectType', projectType),
      ...getNullableQueryParam('status', status),
      ...getNullableQueryParam('ownerPersonId', ownerPersonId),
      ...getNullableQueryParam(
        'isActive',
        isActive === undefined ? undefined : String(isActive)
      ),
    },
  });

export const getProjectById = (id: number): Promise<Project> =>
  request.get(`${PATHS.API.PROJECT}/${id}`);

export const createProject = (project: SaveProjectPayload): Promise<Project> =>
  request.post(PATHS.API.PROJECT, project);

export const updateProject = (
  project: SaveProjectPayload & { id: number }
): Promise<Project> => request.put(PATHS.API.PROJECT, project);

export const getProjectControlLinks = ({
  projectId,
  controlId,
}: {
  projectId?: number;
  controlId?: number;
} = {}): Promise<ProjectControlLink[]> =>
  request.get(PATHS.API.PROJECT_CONTROL_LINKS, {
    query: {
      ...getNullableQueryParam('projectId', projectId),
      ...getNullableQueryParam('controlId', controlId),
    },
  });

export const createProjectControlLink = (
  payload: SaveProjectControlLinkPayload
): Promise<ProjectControlLink> =>
  request.post(PATHS.API.PROJECT_CONTROL_LINK, payload);

export const updateProjectControlLink = (
  payload: SaveProjectControlLinkPayload & { id: number }
): Promise<ProjectControlLink> =>
  request.put(PATHS.API.PROJECT_CONTROL_LINK, payload);

export const deleteProjectControlLink = (
  id: number
): Promise<Response | Error> =>
  request
    .delete(`${PATHS.API.PROJECT_CONTROL_LINK}/${id}`)
    .then((response: Response) => response)
    .catch((error: Error) => error);

export const getProjectSecurityAssetLinks = ({
  projectId,
  securityAssetId,
}: {
  projectId?: number;
  securityAssetId?: number;
} = {}): Promise<ProjectSecurityAssetLink[]> =>
  request.get(PATHS.API.PROJECT_SECURITY_ASSET_LINKS, {
    query: {
      ...getNullableQueryParam('projectId', projectId),
      ...getNullableQueryParam('securityAssetId', securityAssetId),
    },
  });

export const createProjectSecurityAssetLink = (
  payload: SaveProjectSecurityAssetLinkPayload
): Promise<ProjectSecurityAssetLink> =>
  request.post(PATHS.API.PROJECT_SECURITY_ASSET_LINK, payload);

export const updateProjectSecurityAssetLink = (
  payload: SaveProjectSecurityAssetLinkPayload & { id: number }
): Promise<ProjectSecurityAssetLink> =>
  request.put(PATHS.API.PROJECT_SECURITY_ASSET_LINK, payload);

export const deleteProjectSecurityAssetLink = (
  id: number
): Promise<Response | Error> =>
  request
    .delete(`${PATHS.API.PROJECT_SECURITY_ASSET_LINK}/${id}`)
    .then((response: Response) => response)
    .catch((error: Error) => error);

export const getControlScopes = (controlId: number): Promise<ControlScope[]> =>
  request.get(PATHS.API.CONTROL_SCOPES, {
    query: { controlId },
  });

export const saveControlScope = ({
  controlId,
  surfaceId,
  isInScope,
}: {
  controlId: number;
  surfaceId: number;
  isInScope: boolean;
}): Promise<ControlScope> =>
  request.put(PATHS.API.CONTROL_SCOPE, {
    controlId,
    surfaceId,
    isInScope,
  });

export const deleteControlResource = (id: number): Promise<Response | Error> =>
  request
    .delete(getUrl(PATHS.API.CONTROL_RESOURCE, { query: { id } }))
    .then((response: Response) => response)
    .catch((error: Error) => error);

export const getControlBudgetTotals = (
  controlId: number
): Promise<ControlBudgetTotals> =>
  request.get(PATHS.API.CONTROL_BUDGET_TOTALS, {
    query: {
      controlId,
    },
  });

export const getControlBudgetMatrixSummary = (
  controlId: number
): Promise<ControlBudgetMatrixSummary> =>
  request.get(PATHS.API.CONTROL_BUDGET_MATRIX_SUMMARY, {
    query: {
      controlId,
    },
  });

export const getControlBudgetCellBreakdown = ({
  controlId,
  surfaceId,
  threatTier,
}: {
  controlId: number;
  surfaceId: number;
  threatTier: ControlResourceThreatTier;
}): Promise<ControlBudgetCellBreakdown> =>
  request.get(PATHS.API.CONTROL_BUDGET_CELL_BREAKDOWN, {
    query: {
      controlId,
      surfaceId,
      threatTier,
    },
  });

type getKpisAndValuesArgType = {
  groupId?: number;
  groupKey?: string;
  controlId?: number;
  levelId?: number | null;
  surfaceId?: number | null;
  include?: string | undefined;
  date?: string;
};

type getKPIValuesArgType = {
  groupId?: number;
  groupKey?: string;
  controlId?: number;
  levelId?: number | null;
  surfaceId?: number | null;
  include?: string;
  date?: string;
};

export const getKPIValues = ({
  groupId,
  groupKey,
  controlId,
  levelId,
  surfaceId,
  date,
  include,
}: getKPIValuesArgType): Promise<KPIValue[]> => {
  return request
    .get(PATHS.API.KPI_VALUES, {
      query: {
        controlId,
        levelId,
        surfaceId,
        date,
        ...getNullableQueryParam('groupId', groupId),
        ...getNullableQueryParam('groupKey', groupKey),
        ...getNullableQueryParam('include', include),
      },
    })
    .then((kpiValues: KPIValue | KPIValue[]) =>
      ([] as KPIValue[]).concat(kpiValues)
    )
    .catch((response) => {
      if (response.status === 404) {
        return [];
      }
      throw response;
    });
};

export const saveKPIValue = (kpiValue: KPIValue) => {
  const formattedKpiValue = {
    controlId: kpiValue.controlId,
    levelId: kpiValue.levelId,
    surfaceId: kpiValue.surfaceId,
    value:
      kpiValue.value === undefined || kpiValue.value === ''
        ? 0
        : kpiValue.value,
    kpiId: kpiValue.kpiId,
  };

  return request
    .post(PATHS.API.KPI_VALUES, [formattedKpiValue])
    .then(({ data }: { data: KPIValue[] }) => {
      const result = data[0];
      result.value = parseInt(result.value as string);
      return Object.assign(kpiValue, result);
    });
};

export const getKPIsAndValues = async (
  options: getKpisAndValuesArgType
): Promise<KPI[]> => {
  const getKpiValueOptions: getKpisAndValuesArgType = {
    ...options,
  };
  if (options.include === undefined) {
    getKpiValueOptions.include = 'kpis,kpis.valueType,nullvalues,unique';
  }

  return getKPIValues(getKpiValueOptions).then((kpiValues: KPIValue[]) =>
    invertKpiValues(kpiValues)
  );
};

export interface KPISearchFilterType {
  key: string;
  value: string;
  match?: string[];
}

export type SearchObjectType =
  | 'resource'
  | 'securityAsset'
  | 'project'
  | 'person'
  | 'control'
  | 'lens'
  | 'framework';

export type SearchSortField =
  | 'title'
  | 'objectType'
  | 'owner'
  | 'controls'
  | 'environments'
  | 'updatedTime';

export type SearchSortDirection = 'asc' | 'desc';

export interface SearchRelatedItem {
  id: number | string;
  name: string;
}

export interface SearchResult {
  objectType: SearchObjectType;
  id: number | string;
  title: string;
  summary: string | null;
  typeLabel: string;
  status: string | null;
  owner: SearchRelatedItem | null;
  controls: SearchRelatedItem[];
  environments: SearchRelatedItem[];
  lenses: SearchRelatedItem[];
  href: string;
  updatedTime: string | null;
  matchedFields: string[];
  metadata: Record<string, unknown>;
}

export interface SearchFacetBucket {
  value: string;
  label: string;
  count: number;
}

export interface SearchFacets {
  types: SearchFacetBucket[];
  owners: SearchFacetBucket[];
  controls: SearchFacetBucket[];
  environments: SearchFacetBucket[];
  lenses: SearchFacetBucket[];
}

export interface SearchResponse {
  results: SearchResult[];
  facets: SearchFacets;
  total: number;
  limit: number;
  offset: number;
}

export interface SearchObjectsParams {
  q?: string;
  types?: SearchObjectType[];
  controlId?: number;
  ownerPersonId?: number;
  environmentId?: number;
  lensId?: number;
  sortBy?: SearchSortField;
  sortDirection?: SearchSortDirection;
  limit?: number;
  offset?: number;
}

export const searchObjects = ({
  q,
  types,
  controlId,
  ownerPersonId,
  environmentId,
  lensId,
  sortBy,
  sortDirection,
  limit,
  offset,
}: SearchObjectsParams = {}): Promise<SearchResponse> =>
  request.get(PATHS.API.SEARCH, {
    query: {
      ...getNullableQueryParam('q', q),
      ...(types && types.length ? { types: types.join(',') } : {}),
      ...getNullableQueryParam('controlId', controlId),
      ...getNullableQueryParam('ownerPersonId', ownerPersonId),
      ...getNullableQueryParam('environmentId', environmentId),
      ...getNullableQueryParam('lensId', lensId),
      ...getNullableQueryParam('sortBy', sortBy),
      ...getNullableQueryParam('sortDirection', sortDirection),
      ...getNullableQueryParam('limit', limit),
      ...getNullableQueryParam('offset', offset),
    },
  });

export const getKPIMetadata = (): Promise<KPIMetadata[]> =>
  request
    .get(PATHS.API.KPI_METADATA, {
      query: {
        include: 'valueType',
      },
    })
    .then((metadata: KPIMetadata[]) =>
      metadata.sort((a, b) => a.displayOrder - b.displayOrder)
    );

export enum TagHints {
  Default,
  Unused1,
  Unused2,
  ControlContainer,
}

export type TagType = {
  id: number;
  parentId: number | null;
  companyId: number;
  createdByPersonId: number | null;
  ownerPersonId: number | null;
  key: string;
  name: string;
  displayOrder: number;
  subTags: TagType[];
  hint: TagHints;
  visibility: 'private' | 'shared' | 'company';
  isArchived: boolean;
};

const sortNestedTags = (tags: TagType[]): TagType[] => {
  const result = tags.sort((a, b) => a.displayOrder - b.displayOrder);
  result.forEach((tag: TagType) => {
    if (tag.subTags) {
      sortNestedTags(tag.subTags);
    }
  });
  return result;
};

export const getTags = (parentId: number | null = null): Promise<TagType[]> =>
  request
    .get(PATHS.API.TAGS, {
      query: {
        ...getNullableQueryParam('parentId', parentId),
        include: 'subTags',
      },
    })
    .then((tags: TagType[]) => sortNestedTags(tags));

export const getTagById = (id: number): Promise<TagType> =>
  request.get(`${PATHS.API.TAG}/${id}`);

export default {
  login,
  logout,
  getAuthToken,
  refreshAuthToken,
  getCompanies,
  getCompany,
  getUserById,
  getCurrentUser,
  provisionCurrentUser,
  updateCurrentUser,
  changePassword,
};
